/**
 * Sensor Data Grid - 센서 데이터 조회 컴포넌트 (프로토타입)
 * 더미 데이터 사용
 */

import { useState, useMemo } from 'react';
import { 
  useReactTable, 
  getCoreRowModel, 
  getSortedRowModel, 
  getPaginationRowModel, 
  flexRender 
} from '@tanstack/react-table';
import { motion } from 'framer-motion';
import { HiDownload, HiRefresh } from 'react-icons/hi';
import { format } from 'date-fns';
import { mockSensorData, mockSpotsData } from '../data/mockData';

export default function SensorDataGrid() {
  const [selectedSpotId, setSelectedSpotId] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // 필터링된 데이터
  const filteredData = useMemo(() => {
    let data = [...mockSensorData];

    if (selectedSpotId !== 'all') {
      // 스팟별 필터링 (프로토타입에서는 간단히 처리)
      data = data.filter((item, index) => index % 2 === 0);
    }

    if (startDate && endDate) {
      // 날짜 필터링 (프로토타입에서는 간단히 처리)
      data = data.slice(0, Math.min(data.length, 10));
    }

    return data;
  }, [selectedSpotId, startDate, endDate]);

  // 테이블 컬럼 정의
  const columns = useMemo(
    () => [
      {
        accessorKey: 'record_uuid',
        header: 'Record UUID',
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'created_timestamp_utc',
        header: '생성 시점 (UTC)',
        cell: (info) => {
          const value = info.getValue();
          return value ? format(new Date(value), 'yyyy-MM-dd HH:mm:ss') : '-';
        },
      },
      {
        accessorKey: 'measurement_latitude',
        header: '위도',
        cell: (info) => parseFloat(info.getValue()).toFixed(6),
      },
      {
        accessorKey: 'measurement_longitude',
        header: '경도',
        cell: (info) => parseFloat(info.getValue()).toFixed(6),
      },
      {
        accessorKey: 'device_sensor_id',
        header: '기기/센서 ID',
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'original_value',
        header: '원본 측정값',
        cell: (info) => parseFloat(info.getValue()).toFixed(2),
      },
      {
        accessorKey: 'corrected_value',
        header: '보정/산출 후 값',
        cell: (info) => parseFloat(info.getValue()).toFixed(2),
      },
      {
        accessorKey: 'change_actor',
        header: '변경 주체',
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'change_timestamp',
        header: '변경 시점',
        cell: (info) => {
          const value = info.getValue();
          return value ? format(new Date(value), 'yyyy-MM-dd HH:mm:ss') : '-';
        },
      },
      {
        accessorKey: 'change_reason',
        header: '변경 사유',
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'change_version',
        header: '변경 버전',
        cell: (info) => info.getValue(),
      },
    ],
    []
  );

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 50,
      },
    },
  });

  // CSV 다운로드
  const handleDownloadCSV = () => {
    if (!filteredData.length) {
      alert('다운로드할 데이터가 없습니다.');
      return;
    }

    const headers = [
      'Record UUID',
      '생성 시점 (UTC)',
      '위도',
      '경도',
      '기기/센서 ID',
      '원본 측정값',
      '보정/산출 후 값',
      '변경 주체',
      '변경 시점',
      '변경 사유',
      '변경 버전',
    ];

    const csvRows = [
      headers.join(','),
      ...filteredData.map((row) =>
        [
          row.record_uuid,
          row.created_timestamp_utc,
          row.measurement_latitude,
          row.measurement_longitude,
          row.device_sensor_id,
          row.original_value,
          row.corrected_value,
          row.change_actor,
          row.change_timestamp,
          row.change_reason,
          row.change_version,
        ].join(',')
      ),
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const timestamp = format(new Date(), 'yyyyMMdd_HHmmss');
    link.setAttribute('download', `sensor_data_${timestamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      {/* 조회 영역 */}
      <motion.div
        className="rounded-xl shadow-lg border-2 p-6"
        style={{
          background: 'var(--color-surface)',
          borderColor: 'var(--color-primary)',
        }}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="grid grid-cols-1 md:grid-cols-[1fr_min-content_min-content_min-content] gap-4 items-end">
          {/* 스팟 선택 */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
              스팟 선택
            </label>
            <select
              value={selectedSpotId}
              onChange={(e) => setSelectedSpotId(e.target.value)}
              className="w-full px-3 py-2 border-2 rounded-xl text-sm focus:ring-2 transition-all"
              style={{
                borderColor: 'var(--color-primary)',
                background: 'var(--color-background)',
                color: 'var(--color-text)',
              }}
            >
              <option value="all">전체</option>
              {mockSpotsData.map((spot) => (
                <option key={spot.id} value={spot.id}>
                  {spot.spot_name}
                </option>
              ))}
            </select>
          </div>

          {/* 시작일 */}
          <div className="md:w-40">
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
              시작일
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border-2 rounded-xl text-sm focus:ring-2 transition-all"
              style={{
                borderColor: 'var(--color-primary)',
                background: 'var(--color-background)',
                color: 'var(--color-text)',
              }}
            />
          </div>

          {/* 종료일 */}
          <div className="md:w-40">
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
              종료일
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border-2 rounded-xl text-sm focus:ring-2 transition-all"
              style={{
                borderColor: 'var(--color-primary)',
                background: 'var(--color-background)',
                color: 'var(--color-text)',
              }}
            />
          </div>

          {/* 조회 버튼 */}
          <div className="flex justify-end md:justify-start">
            <motion.button
              onClick={() => {}}
              disabled={!startDate || !endDate}
              className="px-6 py-3 text-base font-medium rounded-xl flex items-center gap-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all text-white"
              style={{
                background: `linear-gradient(135deg, var(--color-primary), var(--color-secondary))`,
              }}
              whileHover={{ scale: 1.02, y: -1 }}
              whileTap={{ scale: 0.98 }}
            >
              <HiRefresh className="w-5 h-5" />
              조회
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* 다운로드 버튼 */}
      {filteredData.length > 0 && (
        <motion.div
          className="flex justify-end gap-3"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <motion.button
            onClick={handleDownloadCSV}
            className="px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-2 shadow-lg hover:shadow-xl transition-all text-white"
            style={{
              background: `linear-gradient(135deg, #10b981, #059669)`,
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <HiDownload className="w-4 h-4" />
            CSV 다운로드
          </motion.button>
        </motion.div>
      )}

      {/* 테이블 */}
      <motion.div
        className="rounded-xl shadow-lg border-2 overflow-hidden"
        style={{
          background: 'var(--color-surface)',
          borderColor: 'var(--color-primary)',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        {filteredData.length === 0 ? (
          <div className="p-8 text-center" style={{ color: 'var(--color-text-secondary)' }}>
            조회 조건을 설정하고 조회 버튼을 클릭하세요.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y-2" style={{ borderColor: 'var(--color-primary)' }}>
              <thead style={{ background: `linear-gradient(135deg, var(--color-primary), var(--color-secondary))` }}>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider cursor-pointer hover:opacity-80 transition-colors"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <div className="flex items-center gap-2">
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: <span>↑</span>,
                            desc: <span>↓</span>,
                          }[header.column.getIsSorted()] ?? null}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y-2" style={{ borderColor: 'var(--color-primary)' }}>
                {table.getRowModel().rows.map((row, index) => (
                  <motion.tr 
                    key={row.id} 
                    className="transition-all duration-150 hover:opacity-80"
                    style={{
                      background: index % 2 === 0 ? 'var(--color-surface)' : 'var(--color-background)',
                    }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: index * 0.03 }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-4 py-3 text-sm" style={{ color: 'var(--color-text)' }}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 페이지네이션 */}
        {filteredData.length > 0 && (
          <div className="px-6 py-4 border-t-2 flex items-center justify-between" style={{ borderColor: 'var(--color-primary)', background: 'var(--color-background)' }}>
            <div className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>
              총 <span className="font-bold" style={{ color: 'var(--color-primary)' }}>{filteredData.length}</span>개 중{' '}
              <span className="font-semibold">
                {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}-
                {Math.min(
                  (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                  filteredData.length
                )}
              </span>{' '}
              표시
            </div>
            <div className="flex items-center gap-3">
              <motion.button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="px-4 py-2 text-sm font-semibold rounded-xl border-2 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
                style={{
                  background: 'var(--color-surface)',
                  borderColor: 'var(--color-primary)',
                  color: 'var(--color-text)',
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                이전
              </motion.button>
              <span className="text-sm font-semibold px-3 py-2 rounded-xl border-2" style={{ color: 'var(--color-text)', background: 'var(--color-surface)', borderColor: 'var(--color-primary)' }}>
                페이지 {table.getState().pagination.pageIndex + 1} / {table.getPageCount()}
              </span>
              <motion.button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="px-4 py-2 text-sm font-semibold rounded-xl border-2 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
                style={{
                  background: 'var(--color-surface)',
                  borderColor: 'var(--color-primary)',
                  color: 'var(--color-text)',
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                다음
              </motion.button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
